/* Accessible custom select (lightweight)
 - Adds `-hasJs` modifier to root to hide native select and enable custom UI
 - Adds keyboard support (ArrowUp/Down, Home/End, Enter, Space, Escape)
 - Keeps native select value in sync for form submission
*/

// Module-level: single document listener for all instances (prevents memory leaks)
let documentListenerInitialized = false;

const initDocumentClickListener = () => {
  if (documentListenerInitialized) {
    return;
  }

  document.addEventListener('click', (ev) => {
    const openSelects = document.querySelectorAll('[data-select].-open');

    for (const select of openSelects) {
      if (!select.contains(ev.target)) {
        const trigger = select.querySelector('.js-selectTrigger');
        const list = select.querySelector('.js-selectList');

        if (trigger && list) {
          trigger.setAttribute('aria-expanded', 'false');
          select.classList.remove('-open');
          list.hidden = true;
        }
      }
    }
  }, { passive: true });

  documentListenerInitialized = true;
};

const Select = () => {
  const roots = Array.from(document.querySelectorAll('[data-select]'));

  if (roots.length > 0) {
    roots.forEach((element) => {
      const native = element.querySelector('.a-select__native');
      const trigger = element.querySelector('.js-selectTrigger');
      const list = element.querySelector('.js-selectList');
      const options = Array.from(element.querySelectorAll('.a-select__option'));

      // enable JS UI
      element.classList.add('-hasJs');

      if (!trigger || !list || !native || options.length === 0) {
        return;
      }

      const close = () => {
        trigger.setAttribute('aria-expanded', 'false');
        element.classList.remove('-open');
        list.hidden = true;
      };

      const open = () => {
        trigger.setAttribute('aria-expanded', 'true');
        element.classList.add('-open');
        list.hidden = false;
      };

      const toggle = () => {
        const expanded = trigger.getAttribute('aria-expanded') === 'true';

        if (expanded) {
          close();
        } else {
          open();
        }
      };

      const updateTriggerValue = (value) => {
        const option = options.find(option => option.dataset.value === value);
        const labelEl = trigger.querySelector('.a-select__value');

        if (option) {
          labelEl.textContent = option.textContent.trim();

          if (option.dataset.value === '' || option.classList.contains('-disabled')) {
            labelEl.classList.add('-placeholder');
          } else {
            labelEl.classList.remove('-placeholder');
          }
        } else {
          // fallback to placeholder or empty
          const placeholder = native.querySelector('option[disabled]');

          labelEl.textContent = placeholder ? placeholder.textContent.trim() : '';
        }
      };

      const setValue = (value) => {
        // set native select
        native.value = value;

        // update selected classes and aria-selected
        options.forEach((opt) => {
          const isActive = opt.dataset.value === value;

          opt.classList.toggle('-selected', isActive);
          opt.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });

        updateTriggerValue(value);

        // Handle navigation mode: navigate to value as URL
        if (element.hasAttribute('data-navigate') && value) {
          window.location.href = value;
        }
      };

      // initialize from native value
      if (native.value) {
        updateTriggerValue(native.value);
      }

      // click handlers
      trigger.addEventListener('click', (e) => {
        if (trigger.disabled) {
          return;
        }

        e.preventDefault();
        toggle();
      });

      options.forEach((item, index) => {
        item.addEventListener('click', () => {
          if (item.classList.contains('-disabled')) {
            return;
          }

          setValue(item.dataset.value);
          close();
          trigger.focus();
        });

        item.addEventListener('keydown', (ev) => {
          ev.preventDefault();

          const next = options[index + 1] || options[0];
          const prev = options[index - 1] || options[options.length - 1];

          switch (ev.key) {
            case 'ArrowDown':
              next.focus();
              break;
            case 'ArrowUp':
              prev.focus();
              break;
            case 'Escape':
              close();
              trigger.focus();
              break;
            case 'Enter':
            case ' ':
              item.click();
              break;
          }
        });
      });

      trigger.addEventListener('keydown', (ev) => {
        ev.preventDefault();

        switch (ev.key) {
          case 'ArrowDown':
            open();
            options[0].focus();
            break;
          case 'ArrowUp':
            open();
            options[options.length - 1].focus();
            break;
          case 'Escape':
            close();
            break;
          case 'Enter':
          case ' ':
            toggle();
            break;
        }
      });
    });
  }

  // Initialize single document listener for all select instances
  initDocumentClickListener();
};

export default Select;
